import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, FileText, Building2, Upload, Edit, Trash2, X, Eye, Key, Check, AlertTriangle, Tag } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { API } from "../../utils/format";
import { PercentInput } from "../../components/ui/PercentInput";
import { FieldHint } from "../../components/ui/FieldHint";
import NumberInput from "../../components/NumberInput";
import { DeliveryAddressesEditor } from "../../components/DeliveryAddressesEditor";
import { openAuthenticatedPdfPost } from "../../utils/pdf";
import { NewCustomersAlert } from "../../components/NewCustomersAlert";
import { VatLookupButton } from "../../components/VatLookupButton";
import { PlafondCell, ImportInsuranceDialog, PlafondRequestsDialog, AdvanceExemptButton } from "../../components/plafond";
import { DeliveryBadges, useCapsSummary } from "../../components/logistics/DeliveryInfo";

const AdminCustomersManagement = () => {
  const [customers, setCustomers] = useState([]);
  const capInfo = useCapsSummary(customers.map((c) => c.cap));
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterAgent, setFilterAgent] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [loginCustomer, setLoginCustomer] = useState(null);
  const [showWipe, setShowWipe] = useState(false);
  const [creditMap, setCreditMap] = useState({});
  const [showInsurance, setShowInsurance] = useState(false);
  const [showPlafondReqs, setShowPlafondReqs] = useState(false);
  const [pendingReqCount, setPendingReqCount] = useState(0);

  const loadCredit = async () => {
    try {
      const r = await axios.get(`${API}/customers-credit-summary`);
      setCreditMap(r.data || {});
      setPendingReqCount(Object.values(r.data || {}).filter(x => x.pending_request).length);
    } catch { /* silenzioso */ }
  };
  useEffect(() => { loadCredit(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteSelected, setShowDeleteSelected] = useState(false);

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Eliminare il cliente "${c.business_name}" (${c.codice_cliente})?\nL'operazione è reversibile (soft delete).`)) return;
    try {
      await axios.delete(`${API}/customers/${c.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const r = await axios.get(`${API}/customers?${params}`);
      let list = r.data || [];
      if (filterAgent !== "all") list = list.filter(c => c.agent_id === filterAgent);
      setCustomers(list);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    axios.get(`${API}/agents`).then(r => setAgents(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, filterAgent]);

  // Reset della selezione quando cambia la lista (search/filter)
  useEffect(() => { setSelectedIds(new Set()); }, [search, filterAgent]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allOnPageSelected = customers.length > 0 && customers.every(c => selectedIds.has(c.id));
  const someOnPageSelected = customers.some(c => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        customers.forEach(c => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        customers.forEach(c => next.add(c.id));
        return next;
      });
    }
  };

  return (
    <div>
      <NewCustomersAlert refreshKey={customers} />
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900">Anagrafica Clienti</h2>
          <p className="text-stone-500 text-sm mt-1">{customers.length} clienti totali (tutti gli agenti)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedIds.size > 0 && (
            <Button
              onClick={() => setShowDeleteSelected(true)}
              variant="outline"
              className="border-red-500 bg-red-50 text-red-700 hover:bg-red-100"
              data-testid="admin-delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Elimina {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}
            </Button>
          )}
          <Button onClick={() => setShowWipe(true)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="admin-wipe-customers-btn">
            <AlertTriangle className="w-4 h-4 mr-1" /> Cancella tutti
          </Button>
          <Button onClick={() => setShowPlafondReqs(true)} variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50" data-testid="admin-plafond-requests-btn">
            Richieste plafond{pendingReqCount > 0 ? ` (${pendingReqCount})` : ""}
          </Button>
          <Button onClick={() => setShowInsurance(true)} variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="admin-import-insurance-btn">
            <Upload className="w-4 h-4 mr-1" /> Importa assicurazione
          </Button>
          <Button onClick={() => setShowImport(true)} variant="outline" className="border-[#C5A059] text-[#8A1C2A]" data-testid="admin-bulk-import-btn">
            <FileText className="w-4 h-4 mr-1" /> Importa CSV/XLSX
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="admin-new-customer-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo cliente
          </Button>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Cerca per ragione sociale, P.IVA, email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="admin-customers-search" />
        </div>
        <Select value={filterAgent} onValueChange={setFilterAgent}>
          <SelectTrigger className="w-56 bg-white border-stone-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli agenti</SelectItem>
            {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        {loading ? <div className="p-12 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
          : customers.length === 0 ? <div className="p-12 text-center text-stone-500"><Building2 className="w-12 h-12 mx-auto mb-3 text-stone-300" /><p>Nessun cliente</p><p className="text-xs mt-1">I clienti vengono creati dagli agenti dal loro portale</p></div>
            : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1000px]">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="p-3 w-10">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        ref={el => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 accent-[#8A1C2A] cursor-pointer"
                        aria-label="Seleziona tutti"
                        data-testid="admin-customers-select-all"
                      />
                    </th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">Codice</th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">Ragione sociale</th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">P.IVA / SDI</th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">Plafond</th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">Città</th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">Agente</th>
                    <th className="text-center p-3 text-xs font-semibold text-stone-600">Estero?</th>
                    <th className="text-left p-3 text-xs font-semibold text-stone-600">Listino dedicato</th>
                    <th className="text-center p-3 text-xs font-semibold text-stone-600">Login</th>
                    <th className="text-right p-3 text-xs font-semibold text-stone-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map(c => {
                    const ag = agents.find(a => a.id === c.agent_id);
                    const isSelected = selectedIds.has(c.id);
                    return (
                      <tr key={c.id} className={`border-b border-stone-100 hover:bg-stone-50 ${isSelected ? "bg-red-50/40" : ""}`}>
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(c.id)}
                            className="w-4 h-4 accent-[#8A1C2A] cursor-pointer"
                            aria-label={`Seleziona ${c.business_name}`}
                            data-testid={`admin-customer-select-${c.id}`}
                          />
                        </td>
                        <td className="p-3 font-mono text-xs font-semibold text-[#8A1C2A]">
                          {c.codice_cliente}
                          {c.codice_da_verificare && (
                            <Badge className="ml-1 bg-amber-100 text-amber-800 text-[9px] py-0 font-sans" title={`Il codice ${c.codice_cliente_prev || ""} in TeamSystem appartiene a un altro cliente: verifica il codice corretto`} data-testid={`admin-customer-code-verify-${c.id}`}>
                              da verificare
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 font-medium">{c.business_name}</td>
                        <td className="p-3 text-xs"><div>P.IVA: {c.vat_number}</div>{c.sdi_code && <div className="text-stone-500">SDI: {c.sdi_code}</div>}</td>
                        <td className="p-3">
                          <PlafondCell info={creditMap[c.id]} testId={`plafond-cell-${c.id}`} />
                          <AdvanceExemptButton customer={c} info={creditMap[c.id]} onChanged={loadCredit} />
                        </td>
                        <td className="p-3 text-xs">{c.city} {c.province && `(${c.province})`}{c.cap && <div className="mt-1"><DeliveryBadges info={capInfo(c.cap)} testid={`cust-delivery-${c.id}`} /></div>}</td>
                        <td className="p-3 text-xs">{ag?.name || <Badge className="bg-stone-100 text-stone-600">Diretto Admin</Badge>}</td>
                        <td className="p-3 text-center">{c.is_foreign ? <Badge className="bg-blue-100 text-blue-700">🌍 Estero</Badge> : "—"}</td>
                        <td className="p-3 text-xs">{c.pricelist_type_id ? <Badge className="bg-amber-100 text-amber-700">Dedicato</Badge> : "—"}{c.commission_override != null && <Badge className="bg-purple-100 text-purple-700 ml-1">Provv {Math.round(c.commission_override * 10000) / 100}%</Badge>}</td>
                        <td className="p-3 text-center">{c.has_login ? <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Attivo</Badge> : <Badge variant="outline" className="text-stone-500">Nessuno</Badge>}</td>
                        <td className="p-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs border-stone-300" onClick={() => openAuthenticatedPdfPost(`/customers/${c.id}/name-label-pdf?copies=1`, null, { filename: `Etichetta-${c.business_name || "cliente"}.pdf` })} data-testid={`admin-print-name-label-btn-${c.id}`} title="Stampa etichetta 4×6 con il solo nome del cliente">
                              <Tag className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-stone-300" onClick={() => setEditing(c)} data-testid={`admin-edit-customer-btn-${c.id}`} title="Modifica">
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={() => deleteCustomer(c)} data-testid={`admin-delete-customer-btn-${c.id}`} title="Elimina">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-[#C5A059] text-[#8A1C2A]" onClick={() => setLoginCustomer(c)} data-testid={`admin-create-login-btn-${c.id}`}>
                              <Key className="w-3 h-3 mr-1" /> {c.has_login ? "Reset" : "Login"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
      </Card>
      {creating && <AdminCustomerForm agents={agents} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <AdminCustomerForm agents={agents} initialCustomer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showImport && <BulkImportCustomersDialog agents={agents} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {loginCustomer && <CreateCustomerLoginDialog customer={loginCustomer} onClose={() => setLoginCustomer(null)} onDone={() => { setLoginCustomer(null); load(); }} />}
      {showWipe && <WipeCustomerDataDialog onClose={() => setShowWipe(false)} onDone={() => { setShowWipe(false); load(); }} />}
      <ImportInsuranceDialog open={showInsurance} onClose={() => setShowInsurance(false)} onDone={() => { load(); loadCredit(); }} />
      <PlafondRequestsDialog open={showPlafondReqs} onClose={() => { setShowPlafondReqs(false); loadCredit(); }} />
      {showDeleteSelected && (
        <DeleteSelectedCustomersDialog
          customers={customers.filter(c => selectedIds.has(c.id))}
          onClose={() => setShowDeleteSelected(false)}
          onDone={() => { setShowDeleteSelected(false); setSelectedIds(new Set()); load(); }}
        />
      )}
    </div>
  );
};

const CreateCustomerLoginDialog = ({ customer, onClose, onDone }) => {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if ((password || "").length < 4) { toast.error("Password troppo corta (min 4 caratteri)"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/customers/${customer.id}/create-login`, { password });
      toast.success(r.data?.updated ? "Password aggiornata" : "Login creato");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione login");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white" data-testid="admin-create-login-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-[#8A1C2A]" />{customer.has_login ? "Reset password cliente" : "Crea login cliente"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-stone-50 p-3 rounded-md text-sm">
            <p className="font-semibold">{customer.business_name}</p>
            <p className="text-stone-600 text-xs">Email: <span className="font-mono">{customer.email || <em className="text-red-500">— email mancante in anagrafica —</em>}</span></p>
            {customer.agent_id && <p className="text-stone-500 text-xs mt-1">Cliente associato a un agente</p>}
            {!customer.agent_id && <p className="text-stone-500 text-xs mt-1">Cliente diretto admin — gli ordini saranno approvati dall'admin</p>}
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-700">Password (min. 4 caratteri)</Label>
            <Input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="Imposta una password" data-testid="admin-create-login-password" autoFocus />
            <p className="text-[11px] text-stone-500 mt-1">Comunica la password al cliente via telefono/WhatsApp.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={save} disabled={saving || !customer.email} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="admin-create-login-save">
            {saving ? "Salvataggio..." : customer.has_login ? "Aggiorna password" : "Crea login"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminCustomerForm = ({ agents, onClose, onSaved, initialCustomer }) => {
  const isEdit = !!initialCustomer;
  const [paymentTerms, setPaymentTerms] = useState([]);
  useEffect(() => {
    axios.get(`${API}/payment-terms`).then(r => setPaymentTerms(r.data || [])).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    business_name: initialCustomer?.business_name || "",
    contact_name: initialCustomer?.contact_name || "",
    vat_number: initialCustomer?.vat_number || "",
    fiscal_code: initialCustomer?.fiscal_code || "",
    sdi_code: initialCustomer?.sdi_code || "",
    pec: initialCustomer?.pec || "",
    email: initialCustomer?.email || "",
    phone: initialCustomer?.phone || "",
    iban: initialCustomer?.iban || "",
    address: initialCustomer?.address || "",
    city: initialCustomer?.city || "",
    cap: initialCustomer?.cap || "",
    province: initialCustomer?.province || "",
    is_foreign: !!initialCustomer?.is_foreign,
    agent_id: initialCustomer?.agent_id || "",
    notes: initialCustomer?.notes || "",
    delivery_addresses: initialCustomer?.delivery_addresses || [],
    pricelist_type_id: initialCustomer?.pricelist_type_id || null,
    commission_override: initialCustomer?.commission_override ?? null,
    credit_limit: initialCustomer?.credit_limit ?? 0,
    payment_terms_id: initialCustomer?.payment_terms_id || null,
    codice_cliente: initialCustomer?.codice_cliente || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.business_name || !form.vat_number || !form.address || !form.city) {
      toast.error("Compila i campi obbligatori (Ragione sociale, P.IVA, Indirizzo, Città)"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        agent_id: form.agent_id || null,
        commission_override: form.commission_override === "" || form.commission_override == null ? null : Number(form.commission_override),
      };
      if (isEdit) {
        await axios.put(`${API}/customers/${initialCustomer.id}`, payload);
        toast.success("Cliente aggiornato");
      } else {
        await axios.post(`${API}/customers`, payload);
        toast.success("Cliente creato");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="admin-customer-form">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#8A1C2A]" /> {isEdit ? `Modifica cliente — ${initialCustomer.codice_cliente}` : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {isEdit && (
            <div className="md:col-span-2 flex items-end gap-3 bg-stone-50 border border-stone-200 rounded-md p-2.5">
              <div className="w-48">
                <Label className="flex items-center gap-1">Codice cliente
                  <FieldHint>Deve coincidere con il codice del gestionale (TeamSystem). L&apos;import scadenze lo allinea automaticamente; modificalo a mano solo per correggere un codice provvisorio o errato.</FieldHint>
                </Label>
                <Input value={form.codice_cliente} onChange={e => setForm({ ...form, codice_cliente: e.target.value })} className="bg-white border-stone-300 font-mono" data-testid="cust-codice" />
              </div>
              {initialCustomer?.codice_da_verificare && (
                <p className="text-xs text-amber-800 pb-2" data-testid="cust-codice-verify-note">
                  ⚠️ Codice provvisorio: il precedente <span className="font-mono">{initialCustomer.codice_cliente_prev}</span> in TeamSystem appartiene a un altro cliente. Inserisci il codice corretto.
                </p>
              )}
            </div>
          )}
          <div className="md:col-span-2"><Label>Ragione sociale *</Label><Input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} className="bg-white border-stone-300" data-testid="cust-business-name" /></div>
          <div><Label>P. IVA *</Label>
            <div className="flex gap-1.5">
              <Input value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} className="bg-white border-stone-300" data-testid="cust-vat" />
              <VatLookupButton vat={form.vat_number} currentSdi={form.sdi_code} onFill={(d) => setForm((f) => ({ ...f, ...d }))} />
            </div>
          </div>
          <div><Label>Codice fiscale</Label><Input value={form.fiscal_code} onChange={e => setForm({ ...form, fiscal_code: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Referente</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Codice SDI</Label><Input value={form.sdi_code} onChange={e => setForm({ ...form, sdi_code: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white border-stone-300" data-testid="cust-email" /></div>
          <div><Label>Telefono</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>PEC</Label><Input value={form.pec} onChange={e => setForm({ ...form, pec: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>IBAN</Label><Input value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} className="bg-white border-stone-300" /></div>
          <div className="md:col-span-2"><Label>Indirizzo *</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Città *</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>CAP</Label><Input value={form.cap} onChange={e => setForm({ ...form, cap: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Provincia</Label><Input value={form.province} onChange={e => setForm({ ...form, province: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Estero?
            <FieldHint>
              Imposta a <strong>Sì</strong> per clienti esteri (fuori dall'Italia): l'IVA viene azzerata (0%) sui PDF e sugli ordini. Se No: si applica l'IVA al 10% standard.
            </FieldHint>
          </Label>
            <Select value={form.is_foreign ? "yes" : "no"} onValueChange={v => setForm({ ...form, is_foreign: v === "yes" })}>
              <SelectTrigger className="bg-white border-stone-300"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="no">No (IT, IVA 10%)</SelectItem><SelectItem value="yes">Sì (Estero, IVA 0%)</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="flex items-center gap-1">
              Provvigione dedicata % (opzionale)
              <FieldHint>
                <strong>Provvigione personalizzata su questo cliente</strong>. Ha la priorità più alta nel calcolo. <br/><br/>
                <strong>Ordine di priorità</strong>:<br/>
                1️⃣ <strong>Cliente</strong> (questa) → 2️⃣ Articolo → 3️⃣ Agente → 4️⃣ Listino<br/><br/>
                Lascia vuoto per usare le provvigioni di default.
              </FieldHint>
            </Label>
            <PercentInput value={form.commission_override ?? 0} onChange={(v) => setForm({ ...form, commission_override: v || null })} placeholder="Vuoto = usa default" data-testid="cust-commission-override" />
            <p className="text-[11px] text-stone-500 mt-1">Priorità: Cliente → Articolo → Agente → Listino</p>
          </div>
          <div className="md:col-span-2"><Label className="flex items-center gap-1">
              Agente di riferimento (admin può lasciare vuoto)
              <FieldHint>
                L'agente assegnato al cliente. Riceve la provvigione su tutti gli ordini di questo cliente e può vedere/gestire il cliente dal proprio portale. Lascia vuoto se il cliente è gestito direttamente dall'admin.
              </FieldHint>
            </Label>
            <Select value={form.agent_id || "__none__"} onValueChange={v => setForm({ ...form, agent_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="bg-white border-stone-300" data-testid="cust-agent-select"><SelectValue placeholder="Nessun agente" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nessuno —</SelectItem>
                {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.access_code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Note</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" /></div>

          {/* === INDIRIZZI DI CONSEGNA ALTERNATIVI === */}
          <div className="md:col-span-2 mt-2 pt-3 border-t border-stone-200">
            <DeliveryAddressesEditor
              value={form.delivery_addresses || []}
              onChange={(arr) => setForm({ ...form, delivery_addresses: arr })}
              dataTestidPrefix="admin-cust-delivery"
            />
          </div>

          {/* === CREDIT CONTROL === */}
          <div className="md:col-span-2 mt-2 pt-3 border-t border-stone-200">
            <p className="text-xs font-bold text-stone-700 uppercase tracking-wide mb-1">💳 Credito & Pagamenti</p>
          </div>
          <div><Label className="flex items-center gap-1">
              Plafond credito (€) — 0 = nessun limite
              <FieldHint>
                <strong>Esposizione massima</strong> consentita al cliente. Quando la somma dei documenti aperti supera questo importo, il magazzino vede un <strong>warning rosso</strong> (non blocca la spedizione). Imposta <code>0</code> per nessun limite.
              </FieldHint>
            </Label>
            <NumberInput value={form.credit_limit ?? 0} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })} data-testid="cust-credit-limit" />
          </div>
          <div><Label className="flex items-center gap-1">
              Termini di pagamento
              <FieldHint>
                Determina la <strong>data di scadenza</strong> dei documenti di vendita generati dal magazzino. Se "Anticipato", il magazzino dovrà attendere l'incasso prima di spedire.
              </FieldHint>
            </Label>
            <Select value={form.payment_terms_id || "__none__"} onValueChange={v => setForm({ ...form, payment_terms_id: v === "__none__" ? null : v })}>
              <SelectTrigger className="bg-white border-stone-300" data-testid="cust-payment-terms"><SelectValue placeholder="— Non impostato —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Non impostato —</SelectItem>
                {paymentTerms.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.is_advance ? " 🔒" : ""}{t.end_of_month ? " (FM)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="cust-save-btn">{saving ? "Salvataggio..." : (isEdit ? "Aggiorna cliente" : "Salva cliente")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BulkImportCustomersDialog = ({ agents, onClose, onDone, agentMode = false }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultAgent, setDefaultAgent] = useState("");
  // "skip" (default storico) | "update" | "create"
  const [strategy, setStrategy] = useState("skip");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const uploadFile = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/customers/bulk-import/preview`, fd);
      setPreview(r.data);
      // Auto-map based on header name match (case-insensitive).
      // Includes a few alias hints so common column names ("Agente", "Cliente", ...) match cleanly.
      const aliases = {
        agent_name: ["agente", "agent", "rappresentante", "venditore"],
        agent_code: ["codice agente", "cod agente", "agent code"],
        business_name: ["ragione sociale", "cliente", "nome cliente", "denominazione", "azienda"],
        contact_name: ["referente", "contatto", "responsabile"],
        vat_number: ["partita iva", "p.iva", "piva", "vat"],
        fiscal_code: ["codice fiscale", "cf"],
        email: ["e-mail", "mail"],
        phone: ["telefono", "tel", "cellulare", "mobile"],
        address: ["indirizzo", "via"],
        city: ["città", "citta", "comune"],
        cap: ["cap", "zip"],
        province: ["provincia", "prov"],
        is_foreign: ["estero", "foreign"],
        credit_limit: ["plafond", "fido", "credito", "credit limit", "limite credito"],
        payment_terms: ["termini di pagamento", "pagamento", "termini pagamento", "payment terms", "modalita pagamento", "modalità pagamento"],
      };
      const autoMap = {};
      for (const f of r.data.available_fields) {
        const hintsForKey = aliases[f.key] || [];
        const found = r.data.headers.find(h => {
          const lc = h.toLowerCase().trim();
          if (lc.includes(f.key.toLowerCase())) return true;
          if (lc.includes(f.label.toLowerCase().split(" ")[0])) return true;
          return hintsForKey.some(a => lc === a || lc.includes(a));
        });
        if (found) autoMap[f.key] = found;
      }
      setMapping(autoMap);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); }
    finally { setLoading(false); }
  };

  const confirmImport = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/customers/bulk-import/confirm`, {
        import_id: preview.import_id,
        mapping,
        default_agent_id: defaultAgent || null,
        duplicate_strategy: strategy,
        skip_duplicates: strategy === "skip", // legacy field, mantenuto per compatibilità
      });
      setResult(r.data);
      setStep(3);
      const upd = r.data.updated || 0;
      const ins = r.data.imported || 0;
      toast.success(`Importati ${ins} clienti${upd > 0 ? ` · Aggiornati ${upd}` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore import"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle>📥 Importa clienti (CSV / XLSX) — Step {step}/3</DialogTitle></DialogHeader>
        {step === 1 && (
          <div className="space-y-3">
            <Card className="bg-stone-50 border-stone-200 p-3 text-sm">
              <p className="text-stone-700">Carica un file CSV (separatore `;` o `,`) oppure XLSX. La prima riga deve contenere le intestazioni delle colonne. Nel prossimo step potrai mappare le colonne ai campi del database.</p>
            </Card>
            <Input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0])} className="bg-white border-stone-300" data-testid="bulk-file-input" />
            {file && <p className="text-sm text-stone-600">Selezionato: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
              <Button onClick={uploadFile} disabled={!file || loading} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="bulk-upload-btn">{loading ? "Caricamento..." : "Carica e prosegui →"}</Button>
            </DialogFooter>
          </div>
        )}
        {step === 2 && preview && (
          <div className="space-y-3">
            <Card className="bg-stone-50 border-stone-200 p-3 text-sm">
              <p>📊 <strong>{preview.total_rows}</strong> righe trovate. Colonne rilevate: {preview.headers.length}.</p>
            </Card>
            <div>
              <Label className="text-sm font-semibold">Mappatura colonne</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 max-h-72 overflow-y-auto">
                {preview.available_fields.map(f => (
                  <div key={f.key} className="flex items-center gap-2">
                    <Label className="text-xs w-40">{f.label}{f.required && <span className="text-red-500">*</span>}</Label>
                    <Select value={mapping[f.key] || "__none__"} onValueChange={v => setMapping({ ...mapping, [f.key]: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="h-9 bg-white border-stone-300 flex-1" data-testid={`map-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Non importare —</SelectItem>
                        {preview.headers.map((h, i) => <SelectItem key={i} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-stone-200 pt-3 space-y-2">
              {agentMode ? (
                <p className="text-xs text-stone-600 bg-amber-50 border border-amber-200 rounded p-2">I clienti importati saranno assegnati a <strong>te</strong>. I duplicati (stessa P.IVA) vengono sempre saltati.</p>
              ) : (
              <div>
                <Label className="text-sm">Agente di default (per righe senza codice agente)</Label>
                <Select value={defaultAgent || "__none__"} onValueChange={v => setDefaultAgent(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="bg-white border-stone-300"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessun agente —</SelectItem>
                    {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.access_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="dup-strategy" checked={strategy === "skip"} onChange={() => setStrategy("skip")} className="w-4 h-4 accent-[#8A1C2A]" data-testid="bulk-strategy-skip" />
                <span><strong>Salta duplicati</strong> — se la P.IVA esiste già, ignora la riga (default)</span>
              </label>
              {!agentMode && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="dup-strategy" checked={strategy === "update"} onChange={() => setStrategy("update")} className="w-4 h-4 accent-[#8A1C2A]" data-testid="bulk-strategy-update" />
                  <span><strong>Aggiorna esistenti</strong> — se la P.IVA esiste, sovrascrive i campi cambiati (anagrafica, indirizzo, plafond, termini pagamento…). Non tocca prodotti né listini.</span>
                </label>
              )}
              {!agentMode && (
                <label className="flex items-center gap-2 text-sm text-stone-500">
                  <input type="radio" name="dup-strategy" checked={strategy === "create"} onChange={() => setStrategy("create")} className="w-4 h-4 accent-[#8A1C2A]" data-testid="bulk-strategy-create" />
                  <span>Crea sempre nuovi — anche se duplicato (può generare doppioni, sconsigliato)</span>
                </label>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold">Anteprima prime 5 righe</Label>
              <div className="overflow-x-auto mt-1">
                <table className="text-xs border border-stone-200 min-w-[500px]">
                  <thead><tr>{preview.headers.map(h => <th key={h} className="p-1 border border-stone-200 bg-stone-50">{h}</th>)}</tr></thead>
                  <tbody>{preview.preview.map((row, i) => <tr key={i}>{preview.headers.map(h => <td key={h} className="p-1 border border-stone-200">{row[h]}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} className="border-stone-300">← Indietro</Button>
              <Button onClick={confirmImport} disabled={loading} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="bulk-confirm-btn">{loading ? "Importazione..." : `Importa ${preview.total_rows} righe →`}</Button>
            </DialogFooter>
          </div>
        )}
        {step === 3 && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-green-50 border-green-200 p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{result.imported}</p>
                <p className="text-sm text-green-900">clienti nuovi</p>
              </Card>
              <Card className="bg-blue-50 border-blue-200 p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{result.updated || 0}</p>
                <p className="text-sm text-blue-900">clienti aggiornati</p>
              </Card>
            </div>
            {result.skipped > 0 && <Card className="bg-yellow-50 border-yellow-200 p-3 text-sm"><p>⏭️ {result.skipped} clienti saltati (duplicati P.IVA)</p></Card>}
            {result.auto_created_agents?.length > 0 && (
              <Card className="bg-blue-50 border-blue-200 p-3 text-sm">
                <p className="font-semibold text-blue-900 mb-1">🆕 {result.auto_created_agents.length} agenti creati automaticamente:</p>
                <p className="text-xs text-blue-700">{result.auto_created_agents.join(", ")}</p>
                <p className="text-[11px] text-blue-600 mt-1 italic">Vai in <strong>Anagrafiche → Agenti</strong> per impostare commissione/listino di ciascuno.</p>
              </Card>
            )}
            {result.errors?.length > 0 && (
              <Card className="bg-red-50 border-red-200 p-3 text-sm">
                <p className="font-semibold text-red-900 mb-2">⚠️ {result.errors.length} errori:</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {result.errors.slice(0, 20).map((e, i) => <p key={i} className="text-xs text-red-700">Riga {e.row}: {e.error}</p>)}
                </div>
              </Card>
            )}
            <DialogFooter><Button onClick={onDone} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white">Chiudi e aggiorna lista</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ============== WIPE ALL CUSTOMER DATA DIALOG ==============
const WipeCustomerDataDialog = ({ onClose, onDone }) => {
  const [phrase, setPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const REQUIRED = "ELIMINA TUTTO";

  const confirmWipe = async () => {
    if (phrase.trim() !== REQUIRED) {
      toast.error(`Devi scrivere esattamente: ${REQUIRED}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/wipe-customers-data`, { confirm_phrase: phrase.trim() });
      setResult(r.data.deleted || {});
      toast.success("Dati clienti cancellati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cancellazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-white" data-testid="wipe-customers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Cancella TUTTI i dati clienti
          </DialogTitle>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <Card className="bg-red-50 border-red-200 p-3 text-sm">
              <p className="font-semibold text-red-900 mb-2">⚠️ Operazione IRREVERSIBILE</p>
              <p className="text-red-800">Verranno cancellati definitivamente:</p>
              <ul className="list-disc pl-5 mt-1 text-red-800 text-xs space-y-0.5">
                <li>Tutti i clienti dell&apos;anagrafica</li>
                <li>Tutti gli ordini (storico completo)</li>
                <li>Tutti i documenti di vendita e gli incassi (scadenziario)</li>
                <li>Tutte le offerte dedicate</li>
                <li>Tutte le chat agente↔cliente</li>
                <li>Tutti gli account login dei clienti</li>
              </ul>
              <p className="font-semibold text-green-800 mt-3">✅ NON verranno toccati:</p>
              <ul className="list-disc pl-5 mt-1 text-green-800 text-xs space-y-0.5">
                <li>Prodotti, categorie, listini, prezzi</li>
                <li>Fornitori e ordini fornitore</li>
                <li>Agenti, magazzino, admin</li>
                <li>Termini di pagamento</li>
              </ul>
            </Card>
            <div>
              <Label className="text-sm font-semibold text-stone-800">
                Per procedere scrivi: <code className="bg-stone-100 px-1.5 py-0.5 rounded text-red-700">{REQUIRED}</code>
              </Label>
              <Input
                value={phrase}
                onChange={e => setPhrase(e.target.value)}
                placeholder={REQUIRED}
                className="mt-2 bg-white border-stone-300 font-mono"
                data-testid="wipe-confirm-phrase"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
              <Button
                onClick={confirmWipe}
                disabled={loading || phrase.trim() !== REQUIRED}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="wipe-confirm-btn"
              >
                {loading ? "Cancellazione..." : "Conferma cancellazione"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <Card className="bg-green-50 border-green-200 p-4">
              <p className="text-sm font-semibold text-green-900 mb-2">✅ Cancellazione completata</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-green-800">
                {Object.entries(result).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-green-200 py-1">
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </div>
                ))}
              </div>
            </Card>
            <DialogFooter>
              <Button onClick={onDone} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ============== DELETE SELECTED CUSTOMERS DIALOG ==============
const DeleteSelectedCustomersDialog = ({ customers, onClose, onDone }) => {
  const [phrase, setPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const REQUIRED = "ELIMINA SELEZIONATI";
  const count = customers.length;

  const confirmDelete = async () => {
    if (phrase.trim() !== REQUIRED) {
      toast.error(`Devi scrivere esattamente: ${REQUIRED}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/delete-selected-customers`, {
        customer_ids: customers.map(c => c.id),
        confirm_phrase: phrase.trim(),
      });
      setResult(r.data.deleted || {});
      toast.success(`${r.data.found || 0} clienti cancellati`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cancellazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-white" data-testid="delete-selected-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="w-5 h-5" /> Elimina {count} client{count === 1 ? "e" : "i"} selezionat{count === 1 ? "o" : "i"}
          </DialogTitle>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <Card className="bg-red-50 border-red-200 p-3 text-sm">
              <p className="font-semibold text-red-900 mb-2">⚠️ Operazione IRREVERSIBILE</p>
              <p className="text-red-800">Per ogni cliente selezionato verranno cancellati:</p>
              <ul className="list-disc pl-5 mt-1 text-red-800 text-xs space-y-0.5">
                <li>L&apos;anagrafica cliente</li>
                <li>Tutti i suoi ordini (storico)</li>
                <li>I documenti di vendita e incassi (scadenziario) collegati</li>
                <li>Le offerte dedicate destinate a lui</li>
                <li>L&apos;account login (se presente)</li>
              </ul>
              <p className="text-green-800 mt-2 text-xs">✅ Altri clienti, prodotti, listini, agenti NON verranno toccati.</p>
            </Card>

            <Card className="bg-stone-50 border-stone-200 p-3 max-h-40 overflow-y-auto">
              <p className="text-xs font-semibold text-stone-700 mb-1">Clienti che stai per cancellare:</p>
              <ul className="text-xs text-stone-800 space-y-0.5">
                {customers.slice(0, 20).map(c => (
                  <li key={c.id} className="flex justify-between gap-2 border-b border-stone-200 py-0.5">
                    <span className="truncate">{c.business_name}</span>
                    <span className="font-mono text-[10px] text-stone-500 shrink-0">{c.codice_cliente}</span>
                  </li>
                ))}
                {customers.length > 20 && <li className="text-stone-500 italic">+ altri {customers.length - 20}...</li>}
              </ul>
            </Card>

            <div>
              <Label className="text-sm font-semibold text-stone-800">
                Per procedere scrivi: <code className="bg-stone-100 px-1.5 py-0.5 rounded text-red-700">{REQUIRED}</code>
              </Label>
              <Input
                value={phrase}
                onChange={e => setPhrase(e.target.value)}
                placeholder={REQUIRED}
                className="mt-2 bg-white border-stone-300 font-mono"
                data-testid="delete-selected-confirm-phrase"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
              <Button
                onClick={confirmDelete}
                disabled={loading || phrase.trim() !== REQUIRED}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="delete-selected-confirm-btn"
              >
                {loading ? "Cancellazione..." : `Elimina ${count} client${count === 1 ? "e" : "i"}`}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <Card className="bg-green-50 border-green-200 p-4">
              <p className="text-sm font-semibold text-green-900 mb-2">✅ Cancellazione completata</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-green-800">
                {Object.entries(result).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-green-200 py-1">
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </div>
                ))}
              </div>
            </Card>
            <DialogFooter>
              <Button onClick={onDone} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};



export default AdminCustomersManagement;
export { AdminCustomerForm, BulkImportCustomersDialog };

